import { useState, useEffect, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { COMPANY } from '@/pages/legal/legalConfig'
import { TopicGlyph } from '@/components/gg/TopicGlyph'
import { Emma } from '@/components/emma/Emma'
import { LOCAL_TOPICS, NATIONAL_TOPICS } from '@gogrov/shared'
import '@/styles/gg.css'
import '@/styles/deck2.css'
import '@/styles/emma.css'
import '@/styles/landing2.css'

const d = (seconds: number): CSSProperties => ({ ['--d' as string]: `${seconds}s` } as CSSProperties)

function useScrollReveal() {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('.lp .reveal'))
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'))
      return
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target) }
      }),
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' },
    )
    els.forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [])
}

function Wordmark({ light = false }: { light?: boolean }) {
  return (
    <img
      className="lp-wordmark"
      src={light ? '/brand/logo-horizontal-dark.svg' : '/brand/logo-horizontal-light.svg'}
      alt="GoGrov"
      width={120}
      height={32}
    />
  )
}

function Arrow() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  )
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return <span className="eyebrow eyebrow--accent lp-eyebrow">{children}</span>
}

/* ── Nav ─────────────────────────────────────────────────────── */
function Nav() {
  const [open, setOpen] = useState(false)
  const close = () => setOpen(false)
  return (
    <nav className="lp-nav">
      <div className="lp-wrap lp-nav__inner">
        <a href="#top" aria-label="GoGrov home"><Wordmark /></a>
        <div className={`lp-nav__links${open ? ' is-open' : ''}`}>
          <a href="#how" onClick={close}>How it works</a>
          <a href="#inside" onClick={close}>Inside the app</a>
          <a href="#pricing" onClick={close}>Pricing</a>
          <a href="#forwho" onClick={close}>Who it&rsquo;s for</a>
          <a href="#faq" onClick={close}>FAQ</a>
          <Link to="/login" className="lp-nav__signin" onClick={close}>Sign in</Link>
          <Link to="/signup" className="gg-btn gg-btn--amber lp-nav__cta" onClick={close}>Get started</Link>
        </div>
        <button className="lp-nav__burger" aria-label="Menu" aria-expanded={open} onClick={() => setOpen(!open)}>
          <span /><span /><span />
        </button>
      </div>
    </nav>
  )
}

/* ── Hero ────────────────────────────────────────────────────── */
function Hero() {
  return (
    <header className="lp-hero" id="top">
      <picture>
        <source media="(max-width: 700px)" srcSet="/landing/hero-1200.webp" />
        <img className="lp-hero__photo" src="/landing/hero-2400.webp" alt="A local creator checking her stories on her phone in a park" />
      </picture>
      <div className="lp-hero__scrim" aria-hidden="true" />
      <div className="lp-wrap lp-hero__inner">
        <div className="lp-hero__copy">
          <span className="lp-hero__eyebrow">
            <img src="/brand/sprout-hook-cream.svg" alt="" width={28} height={28} /> GO Grow your voice.
          </span>
          <h1 className="lp-hero__title">Your town&rsquo;s stories, found, written and ready to record.</h1>
          <p className="lp-hero__sub">
            GoGrov searches local records and news for the places you cover, and the national stories your
            audience talks about. Swipe through the headlines, build the ones you want into an article, a script
            and a caption, then read it straight off the teleprompter.
          </p>
          <div className="lp-hero__cta">
            <Link to="/signup" className="gg-btn gg-btn--amber lp-btn-lg">Get started <Arrow /></Link>
            <a href="#how" className="lp-hero__secondary">See how it works</a>
          </div>
          <ul className="lp-hero__meta">
            <li><b>On demand</b><span>Headlines when you ask</span></li>
            <li><b>3 voices</b><span>Newcomer, Reporter, Insider</span></li>
            <li><b>Web, iOS, Android</b><span>Record anywhere</span></li>
          </ul>
        </div>
      </div>
    </header>
  )
}

/* ── Topic ticker ────────────────────────────────────────────── */
function Ticker() {
  const topics = [...LOCAL_TOPICS, ...NATIONAL_TOPICS]
  const row = [...topics, ...topics]
  return (
    <div className="lp-ticker" aria-hidden="true">
      <div className="lp-ticker__track">
        {row.map((t, i) => (
          <span key={i}><TopicGlyph topic={t.id} size={18} />{t.label}</span>
        ))}
      </div>
    </div>
  )
}

/* ── The case ────────────────────────────────────────────────── */
function Thesis() {
  return (
    <section className="lp-section lp-thesis" id="thesis">
      <div className="lp-wrap">
        <Eyebrow>The case</Eyebrow>
        <h2 className="lp-quote reveal">
          The stories are already out there. They just never <em>reach you</em> in time to use them.
        </h2>
        <div className="lp-thesis__grid">
          {[
            ['The hunt', 'Local news is scattered across a dozen sites, feeds and council agendas. Finding what matters in your town takes hours you do not have.'],
            ['The blank page', 'Even once you find a story, writing a tight script from scratch, every single day, is its own full-time job.'],
            ['The drop-off', 'So creators post less than they want to. Momentum fades, and the local voice people trusted goes quiet.'],
          ].map(([t, b], i) => (
            <div className="lp-card reveal" key={t} style={d(i * 0.08)}>
              <h3>{t}</h3>
              <p>{b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ── How it works ────────────────────────────────────────────── */
function How() {
  const steps = [
    {
      title: 'Set your places and your mix',
      body: 'Add the towns and counties you cover, then build a topic mix from 33 topics: nineteen local, fourteen national. It is one 100% budget, so turning one topic up turns the others down. Add keywords to steer each topic toward the names and events you care about.',
    },
    {
      title: 'Pull your headlines',
      body: 'Tap for a fresh deck and Emma searches for you in about a minute. Local stories come from the places you cover, including public records like permits and council agendas. National stories come from the topics you chose. Every card links to its source.',
    },
    {
      title: 'Swipe, then build',
      body: 'Swipe left to pass, right to build. A built story is an article, a script to read on camera and a caption for your post, written in the voice you picked for that topic. Want it another way? Build the same story as a Newcomer, a Reporter or an Insider.',
    },
    {
      title: 'Record and save',
      body: 'Read the script off the teleprompter while you look at the lens. Record in several takes, trim them, redo any one, then save. The clips are stitched into one video in your private library, ready to post wherever you already publish.',
    },
  ]
  return (
    <section className="lp-section lp-how" id="how">
      <div className="lp-wrap">
        <div className="lp-head reveal">
          <div>
            <Eyebrow>How it works</Eyebrow>
            <h2 className="lp-h2">Four steps from nothing to say to a finished video.</h2>
          </div>
          <p className="lp-lead">No blank page, no second screen, no editing timeline.</p>
        </div>
        <ol className="lp-steps">
          {steps.map((s, i) => (
            <li className="lp-step reveal" key={s.title} style={d(i * 0.07)}>
              <span className="lp-step__n">{i + 1}</span>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}

/* ── Inside the app: a live deck card, with Emma ─────────────── */
function Inside() {
  return (
    <section className="lp-section lp-inside" id="inside">
      <div className="lp-wrap lp-inside__grid">
        <div className="reveal">
          <Eyebrow>Inside the app</Eyebrow>
          <h2 className="lp-h2">A deck of your town&rsquo;s stories, and Emma in the corner.</h2>
          <p className="lp-lead">
            Every card shows the topic, the headline, the photo that ran with it and where it came from. When a
            story comes straight from a public record that no outlet has covered yet, it is marked Original: you
            are first, and you can prove it.
          </p>
          <ul className="lp-ticks">
            <li><b>Emma</b> is your advisor. Ask her what to film, how to open a story, or whether your mix is working. Five questions a week are included.</li>
            <li><b>Seeds</b> pay for the work: 12 to build a story, 3 to reroll the deck or rewrite a section, 1 for another voice. Moving your mix around is free.</li>
            <li><b>Your keywords</b> jump to the top of the deck, and seasonal ones glow when their season opens.</li>
          </ul>
          <p className="lp-small">Tap anywhere on this page and Emma will look.</p>
        </div>
        <div className="lp-phone reveal" style={d(0.1)} aria-label="The GoGrov headline deck">
          <div className="lp-phone__screen gg" data-theme="light">
            <div className="lp-phone__header">
              <img src="/brand/logo-horizontal-light.svg" alt="" height={18} />
              <span className="seed-chip lp-phone__chip" aria-hidden="true">
                <svg className="seed-chip__glyph" viewBox="-4 -15 8 30"><path d="M0.4,-14.5 C2.6,-9 3.1,-2 2.6,4 C2.2,9 1.2,12.5 -0.2,14.5 C-1.6,12 -2.6,8 -2.8,3 C-3,-3 -2,-9.5 0.4,-14.5Z" fill="currentColor" /></svg>
                <span>88</span><span className="seed-chip__rule" /><span className="seed-chip__n--gift">20</span><span className="seed-chip__rule" /><span>40</span>
              </span>
            </div>
            <div className="lp-phone__body">
              <span className="eyebrow eyebrow--accent">Today</span>
              <strong className="lp-phone__title">Your headlines</strong>
              <article className="hcard lp-phone__card">
                <span className="hcard__corner hcard__corner--left" aria-hidden="true" />
                <span className="hcard__corner hcard__corner--right" aria-hidden="true" />
                <span className="hcard__pill"><TopicGlyph topic="new-business-openings" size={18} />New Business</span>
                <h3 className="hcard__headline">Bakery owners get the keys to the old bank building on Wall Street</h3>
                <div className="hcard__badges">
                  <span className="gg-badge gg-badge--original">Original</span>
                  <span className="gg-badge">Bend</span>
                </div>
                <div className="hcard__media hcard__media--glyph"><TopicGlyph topic="new-business-openings" size={64} /></div>
                <div className="hcard__foot">
                  <span className="hcard__source">City of Bend permits</span>
                  <span className="hcard__kind">Public record</span>
                </div>
              </article>
            </div>
            <div className="lp-phone__bar">
              <span className="gg-stack-btn">Pass<small>Free</small></span>
              <span className="gg-stack-btn gg-stack-btn--amber">Build<small>12</small></span>
              <span className="gg-stack-btn">Reroll<small>3</small></span>
            </div>
            <div className="lp-phone__emma"><Emma /></div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ── Features ────────────────────────────────────────────────── */
function Features() {
  const feats = [
    { topic: 'civic-government', title: 'Local, national and first', body: 'Three layers in one deck: stories straight from public records, local news for the places you cover, and national stories for the topics your audience follows.' },
    { topic: 'events', title: 'A topic mix that adds up', body: 'Thirty three topics, one 100% budget. Lock the shares you are sure of and the rest rebalance around them. Family friendly and politics settings screen stories before anything is written.' },
    { topic: 'artists-bands', title: 'Three voices', body: 'Newcomer asks. Reporter reports the record. Insider speaks from standing and points you somewhere, never to a verdict. Set a default per topic, or rebuild any story in another voice and keep both.' },
    { topic: 'technology', title: 'Keywords that learn', body: 'Pin the names, places and events you care about. Emma researches each one: whether it is trending, and whether it has a season. Anyone else who adds the same keyword gets that answer free.' },
    { topic: 'arts-culture', title: 'A teleprompter built for one take', body: 'The script scrolls at your pace while you look at the lens. Record in several clips, trim them, redo one, then save a single stitched video to your private library.' },
    { topic: 'nonprofits-causes', title: 'A newsletter from the same stories', body: 'Turn the stories you built into a newsletter in three layouts, with your own brand and sponsor spots you sell directly. GoGrov sends it to the list you keep with us. Included on Grove and Harvest.' },
    { topic: 'local-sports', title: 'Coins, medals and gifts', body: 'Earn coins for showing up, collect the medals for the month&rsquo;s top performer, and get seeds back every year you stay rooted.' },
    { topic: 'finance', title: 'Honest pricing', body: 'Every action shows its price before you tap it. Browsing, passing and tuning your mix are free, and a build that fails costs nothing.' },
    { topic: 'roads-transit', title: 'Web, iOS and Android', body: 'Plan at your desk, record on the phone you already film with. The same deck, stories and library everywhere.' },
  ]
  return (
    <section className="lp-section lp-features" id="features">
      <div className="lp-wrap">
        <div className="lp-head reveal">
          <div>
            <Eyebrow>What you get</Eyebrow>
            <h2 className="lp-h2">Everything you need to post every day.</h2>
          </div>
          <p className="lp-lead">Built for the people who cover a place, not bent out of a general AI tool.</p>
        </div>
        <div className="lp-feat-grid">
          {feats.map((f, i) => (
            <div className="lp-card lp-feat reveal" key={f.title} style={d((i % 3) * 0.07)}>
              <span className="lp-feat__ico"><TopicGlyph topic={f.topic} size={26} /></span>
              <h3>{f.title}</h3>
              <p dangerouslySetInnerHTML={{ __html: f.body }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ── Your voice ──────────────────────────────────────────────── */
function Voice() {
  return (
    <section className="lp-section lp-voice">
      <div className="lp-wrap lp-voice__grid">
        <div className="reveal">
          <Eyebrow>Your voice</Eyebrow>
          <h2 className="lp-h2">We bring the words. You bring the face, the take and the town.</h2>
          <p className="lp-lead">
            Scripts are written to be said out loud, in plain language, with none of the usual AI tells. You
            choose how much authority each topic claims, and every line is yours to change before you record.
          </p>
        </div>
        <div className="lp-promise reveal" style={d(0.08)}>
          <strong>We will never make an AI version of you.</strong>
          <span>No cloned voice, no avatar, no synthetic stand-in, and nothing posted on your behalf. You press record, and you decide what goes out.</span>
        </div>
      </div>
    </section>
  )
}

/* ── Testimonial ─────────────────────────────────────────────── */
function Testimonial() {
  return (
    <section className="lp-testimonial">
      <div className="lp-wrap reveal">
        <img src="/brand/sprout-hook-cream.svg" alt="" width={44} height={44} />
        <blockquote>
          &ldquo;I posted about a new business opening in town. 47 comments, 8 people asking when they&rsquo;re open. That&rsquo;s never happened before.&rdquo;
        </blockquote>
        <cite>Carrie Corcoran · Salem, OR</cite>
      </div>
    </section>
  )
}

/* ── Honest about AI ─────────────────────────────────────────── */
function Honest() {
  const items = [
    ['What it does well', 'Searching local records and news for your places so you do not have to, ranking what it finds against your mix, and drafting a first version you can read on camera.'],
    ['What it does not do', 'Replace your judgment. You decide what gets posted and what the angle is. GoGrov is a research assistant, not an editor.'],
    ['Where stories come from', 'Public records, local outlets and national reporting. It does not scrape social media or make up events, and every story links to a source you can check.'],
    ['Always verify before you record', 'A script is an AI first draft, and a language model can get a name, a date or a number wrong. Read the source, confirm what matters, fix the line, then record.'],
  ]
  return (
    <section className="lp-section lp-honest">
      <div className="lp-wrap">
        <div className="lp-head reveal">
          <div>
            <Eyebrow>Honest about AI</Eyebrow>
            <h2 className="lp-h2">What GoGrov can and cannot do.</h2>
          </div>
          <p className="lp-lead">You deserve a clear picture of what you are actually getting.</p>
        </div>
        <div className="lp-honest__grid">
          {items.map(([t, b], i) => (
            <div className="lp-honest__item reveal" key={t} style={d((i % 2) * 0.07)}>
              <h3>{t}</h3>
              <p>{b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ── Pricing ─────────────────────────────────────────────────── */
function Pricing() {
  const [annual, setAnnual] = useState(false)
  const plans = [
    { name: 'Sprout', tagline: 'Plant your seeds, see what grows.', monthly: 79, annual: 65, seeds: 180, places: 5, topics: 5, keywords: 24, seats: 1, storage: '5 GB', branding: 'Accent colour', newsletter: 'Add-on', cta: 'Start growing' },
    { name: 'Grove', tagline: 'Where your content takes root.', monthly: 149, annual: 121, seeds: 480, places: 10, topics: 10, keywords: 48, seats: 1, storage: '10 GB', branding: 'Accent colour and logo', newsletter: 'Included', cta: 'Grow your grove', highlight: true },
    { name: 'Harvest', tagline: 'Turn your grov into a living.', monthly: 299, annual: 239, seeds: 1200, places: 30, topics: 30, keywords: 96, seats: 15, storage: '50 GB', branding: 'Full white label', newsletter: 'Included', cta: 'Start harvesting' },
  ]
  const rate = [
    ['Browse, pass, tune your mix', 'Free'],
    ['Build a story (article, script, caption)', '12'],
    ['Pull 5 more, or rewrite a section', '3'],
    ['The same story in another position', '1'],
    ['The newsletter version of a story', '1'],
    ['Research a keyword', '1'],
    ['Ask Emma', '5 questions a week included'],
  ]
  return (
    <section className="lp-section lp-pricing" id="pricing">
      <div className="lp-wrap">
        <div className="lp-pricing__head reveal">
          <Eyebrow>Pricing</Eyebrow>
          <h2 className="lp-h2">Every plan is the whole product.</h2>
          <p className="lp-lead">
            What changes is room: how many seeds arrive each week, how many places and topics you cover, and
            how far the branding goes.
          </p>
          <div className="gg-seg lp-toggle" role="group" aria-label="Billing">
            <button aria-pressed={!annual} onClick={() => setAnnual(false)}>Monthly</button>
            <button aria-pressed={annual} onClick={() => setAnnual(true)}>Annual, save 20%</button>
          </div>
        </div>
        <div className="lp-tiers">
          {plans.map((p, i) => (
            <div className={`lp-tier reveal${p.highlight ? ' is-highlight' : ''}`} key={p.name} style={d(i * 0.08)}>
              <h3>{p.name}</h3>
              <p className="lp-tier__tag">{p.tagline}</p>
              <div className="lp-tier__price"><b>${annual ? p.annual : p.monthly}</b><span>/mo</span></div>
              <p className="lp-tier__billed">{annual ? 'billed annually' : 'billed monthly'}</p>
              <Link to="/signup" className={`gg-btn gg-btn--block ${p.highlight ? 'gg-btn--amber' : ''}`}>{p.cta} <Arrow /></Link>
              <dl>
                <div><dt>Seeds each week</dt><dd>{p.seeds}</dd></div>
                <div><dt>Places</dt><dd>{p.places}</dd></div>
                <div><dt>Topics</dt><dd>{p.topics}</dd></div>
                <div><dt>Keywords</dt><dd>{p.keywords}</dd></div>
                <div><dt>Team seats</dt><dd>{p.seats}</dd></div>
                <div><dt>Storage</dt><dd>{p.storage}</dd></div>
                <div><dt>Branding</dt><dd>{p.branding}</dd></div>
                <div><dt>Newsletter</dt><dd>{p.newsletter}</dd></div>
              </dl>
            </div>
          ))}
        </div>
        <div className="lp-rate reveal">
          <h3>What seeds buy</h3>
          <p className="lp-small">Every price shows on its button before you tap it. Weekly seeds are spent first and refill each week; seeds you buy never expire.</p>
          <table>
            <tbody>
              {rate.map(([a, c]) => <tr key={a}><td>{a}</td><td>{c}</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

/* ── Who it's for ────────────────────────────────────────────── */
function ForWho() {
  const people = [
    { img: '/creator-2.png', title: 'Community publishers', body: 'You already run the page people check when something happens in town. Weight civic, schools and safety, and be first to the council agenda without spending the morning hunting.' },
    { img: '/creator-1.png', title: 'Neighbourhood guides', body: 'People want to know what a place is actually like. Post a short update on what is opening, changing and happening most days, and become the person they check first.' },
    { img: '/creator-4.png', title: 'Tourism boards', body: 'Weight events, food, arts, outdoors and tourism across the county, and a small team can publish daily instead of weekly.' },
    { img: '/creator-7.png', title: 'Local creators', body: 'You have the audience and the personality. GoGrov hands you something worth talking about every day, and you bring the take.' },
    { img: '/creator-5.png', title: 'Local journalists', body: 'Scan your whole beat with every source linked, then turn the stories you were going to write anyway into a video version.' },
    { img: '/creator-6.png', title: 'Sports and events voices', body: 'Mix local teams with the national leagues your followers talk about, and let seasonal keywords tell you when to lean in.' },
  ]
  return (
    <section className="lp-section lp-forwho" id="forwho">
      <div className="lp-wrap">
        <div className="lp-head reveal">
          <div>
            <Eyebrow>Who it&rsquo;s for</Eyebrow>
            <h2 className="lp-h2">For the people who are the local voice.</h2>
          </div>
          <p className="lp-lead">Different beats, same problem: the stories exist, but finding and writing them eats the day.</p>
        </div>
        <div className="lp-people">
          {people.map((p, i) => (
            <div className="lp-person reveal" key={p.title} style={d((i % 3) * 0.07)}>
              <img src={p.img} alt="" loading="lazy" />
              <div>
                <h3>{p.title}</h3>
                <p>{p.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ── FAQ ─────────────────────────────────────────────────────── */
function Faq() {
  const faqs = [
    ['How does GoGrov find stories for my town?', 'When you ask for headlines, Emma searches for the places you cover and the topics in your mix: public records like permits, planning and council agendas, local outlets, and national reporting for your national topics. Each story is checked against its source before it reaches your deck, and every card links to it.'],
    ['What does Original mean on a card?', 'The story came straight from a public record, and we could not find an outlet that had covered it yet. You are first, and the source is right there to prove it. If coverage does exist, the card says Corroborated instead.'],
    ['Is the script accurate? Do I need to check it?', 'Yes, always check it. The script is a first draft written by an AI language model from the source, and models can get a name, a date or a number wrong. Read the source, confirm anything you will say on camera, and correct the script before you record.'],
    ['What are seeds?', 'Seeds are how GoGrov is priced. Your plan brings a fresh set each week, spent first. Building a story is 12, a reroll or rewrite is 3, another voice is 1. Browsing, passing and changing your mix are free, and if a build fails you are not charged.'],
    ['Who is Emma?', 'Emma is the advisor in the corner of the app. Ask her what to film this week, how to open a story, or whether your mix is working. Five questions a week are included; after that each one is a seed.'],
    ['Can I change the voice of the scripts?', 'Yes. Each topic has a default voice: Newcomer, Reporter or Insider. Change the default in Settings, or rebuild any single story in another voice for one seed. The original is always kept, and switching back is free.'],
    ['How long does it take each day?', 'A fresh deck takes about a minute to arrive. Swiping through it is a few minutes, building a story takes under a minute, and a short clip is a few minutes more including a retake. It is meant to fit inside a coffee.'],
    ['Do I need editing experience or equipment?', 'No. Record one clip or several, trim the ends, redo any take, and save: the clips are stitched together for you. A phone and a reasonably quiet room are enough to start.'],
    ['Who owns the videos I record?', 'You do. Post, sell, licence or delete them wherever you publish. GoGrov takes no cut, never publishes on your behalf, and does not use your recordings to train AI models.'],
    ['Is there a newsletter?', 'Yes. Turn the stories you built into a newsletter in three layouts, with your own brand and sponsor spots you sell directly; sponsors pay you and GoGrov takes no cut. GoGrov sends it to the list you keep with us, and it is included on Grove and Harvest.'],
    ['How do I get started?', 'GoGrov is onboarding publishers by invitation. If you have an invite code, create your account on this site, choose your places and topics, and your first deck is a minute away. If you do not have a code yet, tell us about your beat on the contact page.'],
  ]
  return (
    <section className="lp-section lp-faq" id="faq">
      <div className="lp-wrap lp-faq__wrap">
        <div className="lp-head reveal">
          <div>
            <Eyebrow>Questions</Eyebrow>
            <h2 className="lp-h2">Frequently asked questions.</h2>
          </div>
          <p className="lp-lead">Straight answers, including the less flattering ones.</p>
        </div>
        <div className="lp-faq__list">
          {faqs.map(([q, a]) => (
            <details className="lp-faq__item" key={q}>
              <summary><h3>{q}</h3><span aria-hidden="true" /></summary>
              <p>{a}</p>
            </details>
          ))}
        </div>
        <p className="lp-small lp-faq__foot">Still have a question? <Link to="/contact">Get in touch</Link>.</p>
      </div>
    </section>
  )
}

/* ── CTA ─────────────────────────────────────────────────────── */
function Cta() {
  return (
    <section className="lp-cta">
      <div className="lp-wrap reveal">
        <img src="/brand/sprout-hook.svg" alt="" width={72} height={72} />
        <h2>Cover your community <em>every day.</em></h2>
        <p>Choose your places and topics, and your first deck is a minute away. GoGrov is onboarding publishers by invitation, so bring the code from your welcome email.</p>
        <div className="lp-cta__btns">
          <Link to="/signup" className="gg-btn gg-btn--amber lp-btn-lg">Get started <Arrow /></Link>
          <Link to="/login" className="lp-hero__secondary lp-cta__signin">Already have an account? Sign in</Link>
        </div>
        <p className="lp-small">No invite code yet? <Link to="/contact">Tell us about your beat</Link> and we will be in touch when a place opens.</p>
      </div>
    </section>
  )
}

/* ── Footer ──────────────────────────────────────────────────── */
function Footer() {
  return (
    <footer className="lp-foot">
      <div className="lp-wrap">
        <div className="lp-foot__grid">
          <div className="lp-foot__brand">
            <Wordmark light />
            <p>Your town&rsquo;s stories, found, written and ready to record.</p>
          </div>
          <div className="lp-foot__col">
            <h5>Product</h5>
            <a href="#how">How it works</a>
            <a href="#inside">Inside the app</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
          </div>
          <div className="lp-foot__col">
            <h5>Account</h5>
            <Link to="/login">Sign in</Link>
            <Link to="/signup">Create an account</Link>
          </div>
          <div className="lp-foot__col">
            <h5>Company</h5>
            <Link to="/contact">Contact</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
          </div>
        </div>
        <div className="lp-foot__bottom">
          <span>© {new Date().getFullYear()} {COMPANY.legalName}. All rights reserved.</span>
          <span>Cultivated locally · Est. 2026</span>
        </div>
      </div>
    </footer>
  )
}

export function Landing() {
  useScrollReveal()
  return (
    <div className="gg lp" data-theme="light">
      <Nav />
      <Hero />
      <Ticker />
      <Thesis />
      <How />
      <Inside />
      <Features />
      <Voice />
      <Testimonial />
      <Honest />
      <Pricing />
      <ForWho />
      <Faq />
      <Cta />
      <Footer />
    </div>
  )
}
